import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';
import { AppLayout } from '../components/layout/AppLayout';
import { fetchMonth, listMonths, fetchMonthOwnerBalances, MonthOwnerBalance } from '../services/monthsService';
import { Month } from '../types/month';
import { getPendingPaymentsTotal } from '../services/pendingPaymentsService';
import { getMonthlyGoal, updateMonthlyGoal, MonthlyGoal } from '../services/monthlyGoalService';
import { ConfirmDialog } from '../components/ConfirmDialog';

export const DashboardPage = () => {
  const { user, refreshProfile, token } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const greeting = useMemo(() => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Morning';
    if (hour < 17) return 'Afternoon';
    if (hour < 21) return 'Evening';
    return 'Hello';
  }, []);

  const currentMonthISO = () => {
    const now = new Date();
    const periodAnchor = now.getDate() < 11 ? new Date(now.getFullYear(), now.getMonth() - 1, 1) : new Date(now.getFullYear(), now.getMonth(), 1);
    return `${periodAnchor.getFullYear()}-${String(periodAnchor.getMonth() + 1).padStart(2, '0')}`;
  };

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentMonth, setCurrentMonth] = useState<Month | null>(null);
  const [months, setMonths] = useState<Month[]>([]);
  const [ownerBalances, setOwnerBalances] = useState<Record<string, MonthOwnerBalance>>({});
  const [displayYear, setDisplayYear] = useState<number>(0);
  const [displayMonth, setDisplayMonth] = useState<number>(0);
  const [pendingPaymentsTotal, setPendingPaymentsTotal] = useState<number>(0);
  const [monthlyGoal, setMonthlyGoal] = useState<MonthlyGoal | null>(null);
  const [goalEditOpen, setGoalEditOpen] = useState(false);
  const [goalEditValue, setGoalEditValue] = useState<string>('');
  const [goalSaving, setGoalSaving] = useState(false);

  // Memoize filtered months and totals calculation
  const { filteredMonths, totals } = useMemo(() => {
    const today = new Date();
    const isMonthFinalized = (year: number, month: number): boolean => {
      const nextMonth = month === 12 ? 1 : month + 1;
      const nextMonthYear = month === 12 ? year + 1 : year;
      const finalizedDate = new Date(nextMonthYear, nextMonth - 1, 10);
      return today >= finalizedDate;
    };

    const filtered = months
      .filter((m) => !(m.year === 2025 && m.month === 7))
      .filter((m) => m.year > 2025 || (m.year === 2025 && m.month >= 8))
      .filter((m) => isMonthFinalized(m.year, m.month));
    
    const totals = filtered.reduce(
      (acc, m) => {
        const allocations = m.allocations || [];
        const gauravAllocation = allocations.find((a) => {
          const owner = typeof a.owner === 'string' ? a.owner : String(a.owner);
          return owner === 'gaurav' || owner.toLowerCase() === 'gaurav';
        })?.allocation_amount || '0';
        
        const sourabhAllocation = allocations.find((a) => {
          const owner = typeof a.owner === 'string' ? a.owner : String(a.owner);
          return owner === 'sourabh' || owner.toLowerCase() === 'sourabh';
        })?.allocation_amount || '0';
        
        return {
          totalEarning: acc.totalEarning + Number(m.total_earning ?? 0),
          totalExpense: acc.totalExpense + Number(m.total_expense ?? 0),
          totalProfit: acc.totalProfit + Number(m.profit ?? 0),
          totalGaurav: acc.totalGaurav + Number(gauravAllocation),
          totalSourabh: acc.totalSourabh + Number(sourabhAllocation),
        };
      },
      { totalEarning: 0, totalExpense: 0, totalProfit: 0, totalGaurav: 0, totalSourabh: 0 }
    );
    
    return { filteredMonths: filtered, totals };
  }, [months]);

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }

    refreshProfile().catch(() => undefined);
  }, [navigate, refreshProfile, token]);

  useEffect(() => {
    const load = async () => {
      if (!token) return;
      setLoading(true);
      setError(null);
      try {
        // load current month summary
        const iso = currentMonthISO();
        const [year, month] = iso.split('-').map((v) => Number(v));
        const [mRes, listRes, balancesRes, pendingTotalRes, goalRes] = await Promise.all([
          fetchMonth(token, year, month),
          listMonths(token),
          fetchMonthOwnerBalances(token, year, month),
          getPendingPaymentsTotal(token, 'pending'),
          getMonthlyGoal(token, year, month),
        ]);
        setCurrentMonth(mRes.data);
        setMonths(listRes.data);
        setOwnerBalances(balancesRes.data);
        setPendingPaymentsTotal(Number(pendingTotalRes.data.total));
        setMonthlyGoal(goalRes.data);
        setDisplayYear(year);
        setDisplayMonth(month);
      } catch (e) {
        const errorMessage = e instanceof Error ? e.message : 'Failed to load dashboard';
        setError(errorMessage);
        showToast(errorMessage, 'error');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token]);

  return (
    <AppLayout title={`${greeting}, ${user?.name?.split(' ')[0] ?? 'partners'}`}>
      <section className="cards">
        <article className="card">
          <span className="badge">Current month</span>
          <h3 style={{ margin: '6px 0', fontSize: '1.6rem' }}>
            ₹ {Number(currentMonth?.total_earning ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
          </h3>
          <p className="subtle-text">Total earning</p>
        </article>

        <article className="card">
          <span className="badge" style={{ background: 'rgba(56, 189, 248, 0.16)' }}>Outflow</span>
          <h3 style={{ margin: '6px 0', fontSize: '1.6rem' }}>
            ₹ {Number(currentMonth?.total_expense ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
          </h3>
          <p className="subtle-text">Total expense</p>
        </article>

        {/* <article className="card">
          <span className="badge" style={{ background: 'rgba(34, 197, 94, 0.16)', color: '#15803d' }}>Computed Profit</span>
          <h3 style={{ margin: '6px 0', fontSize: '1.6rem' }}>
            ₹ {Number(currentMonth?.profit ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
          </h3>
          <p className="subtle-text">
            Last computed {currentMonth?.computed_at ? new Date(currentMonth.computed_at).toLocaleString() : '—'}
          </p>
        </article> */}

        <article className="card" style={{ background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.08) 0%, rgba(248, 113, 113, 0.08) 100%)' }}>
          <span className="badge" style={{ background: 'rgba(239, 68, 68, 0.16)', color: '#dc2626' }}>Pending</span>
          <h3 style={{ margin: '6px 0', fontSize: '1.6rem' }}>
            ₹ {pendingPaymentsTotal.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
          </h3>
          <p className="subtle-text">Total pending payments</p>
        </article>

        <article className="card" style={{ background: 'linear-gradient(135deg, rgba(168, 85, 247, 0.08) 0%, rgba(192, 132, 252, 0.08) 100%)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
            <span className="badge" style={{ background: 'rgba(168, 85, 247, 0.16)', color: '#9333ea' }}>Goal Tracker</span>
            <button
              type="button"
              onClick={() => {
                setGoalEditValue(monthlyGoal?.goal_amount ?? '0');
                setGoalEditOpen(true);
              }}
              style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                padding: '4px 8px',
                fontSize: '0.85rem',
                color: '#9333ea',
                textDecoration: 'underline',
              }}
            >
              Edit
            </button>
          </div>
          <h3 style={{ margin: '6px 0', fontSize: '1.6rem' }}>
            {monthlyGoal?.percentage.toFixed(1) ?? '0.0'}%
          </h3>
          <p className="subtle-text">
            ₹ {Number(monthlyGoal?.received_amount ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })} / ₹ {Number(monthlyGoal?.goal_amount ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
          </p>
          <div style={{ marginTop: 12, height: 8, background: 'rgba(168, 85, 247, 0.1)', borderRadius: 4, overflow: 'hidden' }}>
            <div
              style={{
                height: '100%',
                width: `${Math.min(100, monthlyGoal?.percentage ?? 0)}%`,
                background: 'linear-gradient(90deg, #9333ea, #a855f7)',
                borderRadius: 4,
                transition: 'width 0.3s ease',
              }}
            />
          </div>
        </article>
      </section>

      <section className="cards" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}>
        <h3 style={{ gridColumn: '1 / -1', margin: '0 0 12px', fontSize: '1.1rem', fontWeight: 600 }}>
          Owner Balances
          <span style={{ fontSize: '0.85rem', fontWeight: 400, color: 'rgba(21, 37, 58, 0.6)', marginLeft: 8 }}>
            — Balance as of {displayMonth > 0 ? `${String(displayMonth).padStart(2, '0')}/${displayYear}` : 'current month'}
          </span>
        </h3>
        {(['gaurav', 'sourabh', 'company'] as const).map((key) => {
          const balance = ownerBalances[key] ?? {
            previous_month_balance: 0,
            current_month_opening_balance: 0,
            current_month_earnings: 0,
            current_month_expenses: 0,
            current_month_profit_allocation: 0,
            current_month_owner_draws: 0,
            current_month_balance: 0,
          };
          const label = key === 'gaurav' ? 'Gaurav N' : key === 'sourabh' ? 'Sourabh S' : 'Company';
          return (
            <article className="card" key={`balance-${key}`} style={{ background: 'linear-gradient(135deg, rgba(11, 77, 153, 0.04) 0%, rgba(31, 162, 255, 0.04) 100%)' }}>
              <h3 style={{ margin: 0, fontSize: '1.1rem' }}>{label}</h3>
              <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem' }}>
                  <span className="subtle-text">Previous Month Balance:</span>
                  <strong>₹ {balance.previous_month_balance.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</strong>
                </div>
                {(balance.current_month_opening_balance ?? 0) > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem' }}>
                    <span className="subtle-text">Opening Balance (This Month):</span>
                    <strong style={{ color: '#15803d' }}>+ ₹ {(balance.current_month_opening_balance ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</strong>
                  </div>
                )}
                {key === 'company' ? (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem' }}>
                      <span className="subtle-text">This Month Earnings:</span>
                      <strong style={{ color: '#15803d' }}>+ ₹ {balance.current_month_earnings.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem' }}>
                      <span className="subtle-text">This Month Expenses:</span>
                      <strong style={{ color: '#dc2626' }}>- ₹ {balance.current_month_expenses.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</strong>
                    </div>
                  </>
                ) : (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem' }}>
                      <span className="subtle-text">This Month Earnings:</span>
                      <strong style={{ color: '#dc2626' }}>- ₹ {balance.current_month_earnings.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem' }}>
                      <span className="subtle-text">This Month Expenses:</span>
                      <strong style={{ color: '#15803d' }}>+ ₹ {balance.current_month_expenses.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</strong>
                    </div>
                  </>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem' }}>
                  <span className="subtle-text">Profit Allocation:</span>
                  <strong style={{ color: '#15803d' }}>+ ₹ {balance.current_month_profit_allocation.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</strong>
                </div>
                {balance.current_month_owner_draws !== undefined && balance.current_month_owner_draws > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem' }}>
                    <span className="subtle-text">Owner Draws:</span>
                    <strong style={{ color: '#dc2626' }}>- ₹ {balance.current_month_owner_draws.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</strong>
                  </div>
                )}
                <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid rgba(15, 35, 95, 0.1)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontWeight: 600 }}>Current Month Balance:</span>
                    <h3 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700, color: balance.current_month_balance >= 0 ? '#15803d' : '#dc2626' }}>
                      ₹ {balance.current_month_balance.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                    </h3>
                  </div>
                </div>
              </div>
            </article>
          );
        })}
      </section>

      <section className="card" style={{ padding: 0 }}>
        <header style={{ padding: '16px 20px' }}>
          <h3 style={{ margin: 0 }}>All months</h3>
        </header>
        <div className="table">
          <div className="table-head" style={{ gridTemplateColumns: '160px 1fr 1fr 1fr 1fr 1fr', gap: '10px' }}>
            <span>Month</span>
            <span>Total earning</span>
            <span>Total expense</span>
            <span>Total profit</span>
            <span>Profit Gaurav</span>
            <span>Profit Sourabh</span>
          </div>
          {loading ? (
            <div className="table-row" style={{ gridTemplateColumns: '1fr' }}>
              <span style={{ textAlign: 'center' }}>Loading…</span>
            </div>
          ) : months.length === 0 ? (
            <div className="table-row" style={{ gridTemplateColumns: '1fr' }}>
              <span style={{ textAlign: 'center' }}>No month records yet.</span>
            </div>
          ) : (
            <>
              <div className="table-row" style={{ gridTemplateColumns: '160px 1fr 1fr 1fr 1fr 1fr', gap: '10px', fontWeight: 700, background: 'rgba(11, 77, 153, 0.08)', borderBottom: '2px solid rgba(11, 77, 153, 0.2)' }}>
                <span>Total</span>
                <span>₹ {totals.totalEarning.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
                <span>₹ {totals.totalExpense.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
                <span>₹ {totals.totalProfit.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
                <span>₹ {totals.totalGaurav.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
                <span>₹ {totals.totalSourabh.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
              </div>
              {filteredMonths.map((m) => {
                const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                const monthName = monthNames[m.month - 1] || String(m.month).padStart(2, '0');
                
                const allocations = m.allocations || [];
                const gauravAllocation = allocations.find((a) => {
                  const owner = typeof a.owner === 'string' ? a.owner : String(a.owner);
                  return owner === 'gaurav' || owner.toLowerCase() === 'gaurav';
                })?.allocation_amount || '0';
                
                const sourabhAllocation = allocations.find((a) => {
                  const owner = typeof a.owner === 'string' ? a.owner : String(a.owner);
                  return owner === 'sourabh' || owner.toLowerCase() === 'sourabh';
                })?.allocation_amount || '0';
                
                return (
                  <div key={m.id} className="table-row" style={{ gridTemplateColumns: '160px 1fr 1fr 1fr 1fr 1fr', gap: '10px' }}>
                    <span>{monthName} {m.year}</span>
                    <span>₹ {Number(m.total_earning ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
                    <span>₹ {Number(m.total_expense ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
                    <span>₹ {Number(m.profit ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
                    <span>₹ {Number(gauravAllocation).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
                    <span>₹ {Number(sourabhAllocation).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
                  </div>
                );
              })}
            </>
          )}
        </div>
      </section>

      {goalEditOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: 20,
          }}
          onClick={() => setGoalEditOpen(false)}
        >
          <div
            className="card"
            style={{
              maxWidth: 400,
              width: '100%',
              padding: 24,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 16px' }}>Edit Monthly Goal</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 20 }}>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <span style={{ fontSize: '0.9rem', fontWeight: 500 }}>Goal Amount (₹)</span>
                <input
                  type="number"
                  className="input"
                  value={goalEditValue}
                  onChange={(e) => setGoalEditValue(e.target.value)}
                  placeholder="Enter goal amount"
                  min="0"
                  step="0.01"
                  style={{ width: '100%' }}
                />
              </label>
            </div>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setGoalEditOpen(false)}
                disabled={goalSaving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn"
                onClick={async () => {
                  if (!token || !displayYear || !displayMonth) return;
                  const goalAmount = parseFloat(goalEditValue);
                  if (isNaN(goalAmount) || goalAmount < 0) {
                    showToast('Please enter a valid goal amount', 'error');
                    return;
                  }
                  setGoalSaving(true);
                  try {
                    const response = await updateMonthlyGoal(token, displayYear, displayMonth, goalAmount);
                    setMonthlyGoal(response.data);
                    setGoalEditOpen(false);
                    showToast('Goal updated successfully', 'success');
                  } catch (e) {
                    showToast(e instanceof Error ? e.message : 'Failed to update goal', 'error');
                  } finally {
                    setGoalSaving(false);
                  }
                }}
                disabled={goalSaving}
              >
                {goalSaving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
};

